(() => {
  'use strict';

  function initOrderStory() {
    const root = document.getElementById('order-story');
    if (!root || root.dataset.ready === 'true') return;

    const nav = root.querySelector('.os-step-nav');
    const buttons = [...root.querySelectorAll('.os-step-button')];
    const copies = [...root.querySelectorAll('.os-copy')];
    const workspace = root.querySelector('.os-workspace');
    const controls = root.querySelector('.os-controls');
    const play = root.querySelector('.os-play');
    const playLabel = root.querySelector('.os-play-label');
    const next = root.querySelector('.os-next');
    const count = root.querySelector('.os-scene-count');
    const caption = root.querySelector('.os-scene-caption');
    const live = root.querySelector('.os-live');
    if (!nav || !workspace || !controls || !play || !playLabel || !next ||
        !count || !caption || !live || buttons.length !== 4 || copies.length !== 4) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const stepDuration = 4500;
    const titles = [
      'Choose your cut line',
      'Choose your material and finish',
      'Choose your size and quantity',
      'Upload your image'
    ];
    const captions = [
      'A cut line that follows your artwork.',
      'Catch the light. Find your finish.',
      'Your size. Your stack.',
      'Artwork to proof. A preview of the process.'
    ];
    let step = 0;
    let paused = reduced.matches;
    let inView = false;
    let pageActive = true;
    let timer = 0;
    let startedAt = 0;
    let remaining = stepDuration;

    function stopClock() {
      if (!timer) return;
      window.clearTimeout(timer);
      timer = 0;
      remaining = Math.max(0, remaining - (performance.now() - startedAt));
    }

    function syncPlayback() {
      stopClock();
      const visible = inView && pageActive && !document.hidden;
      const playing = visible && !paused && !reduced.matches;
      root.dataset.visible = String(visible);
      root.dataset.playing = String(playing);
      root.dataset.paused = String(paused || reduced.matches);
      root.dataset.reduced = String(reduced.matches);
      play.disabled = reduced.matches;
      playLabel.textContent = reduced.matches ? 'Reduced motion' : paused ? 'Resume tour' : 'Pause tour';
      play.setAttribute('aria-label', reduced.matches
        ? 'Automatic tour is off for reduced motion. Use the step buttons or Next step.'
        : paused ? 'Resume the guided tour' : 'Pause the guided tour');

      if (!playing) return;
      startedAt = performance.now();
      timer = window.setTimeout(() => {
        timer = 0;
        // Background tabs never advance a step, even if a timer was queued.
        if (document.hidden || !pageActive || !inView || paused || reduced.matches) {
          remaining = stepDuration;
          syncPlayback();
          return;
        }
        selectStep((step + 1) % buttons.length, false);
      }, remaining);
    }

    function selectStep(index, manual) {
      stopClock();
      step = index;
      remaining = stepDuration;
      if (manual) paused = true;
      root.dataset.step = String(step + 1);
      root.dataset.manual = String(manual || reduced.matches);
      buttons.forEach((button, index) => {
        button.setAttribute('aria-pressed', String(index === step));
        copies[index].hidden = index !== step;
      });
      count.textContent = `0${step + 1} / 04`;
      caption.textContent = captions[step];
      next.setAttribute('aria-label', `Next step: ${titles[(step + 1) % buttons.length]}`);
      if (manual) {
        live.textContent = `${copies[step].textContent.replace(/\s+/g, ' ').trim()} Tour paused.`;
      } else {
        live.textContent = '';
      }
      syncPlayback();
    }

    function measureVisibility() {
      const rect = workspace.getBoundingClientRect();
      const height = window.innerHeight || document.documentElement.clientHeight;
      const width = window.innerWidth || document.documentElement.clientWidth;
      const visibleHeight = Math.max(0, Math.min(rect.bottom, height) - Math.max(rect.top, 0));
      const visibleWidth = Math.max(0, Math.min(rect.right, width) - Math.max(rect.left, 0));
      const area = Math.max(1, rect.width * rect.height);
      inView = visibleHeight * visibleWidth / area >= .2;
      syncPlayback();
    }

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => selectStep(index, true));
    });

    nav.addEventListener('keydown', event => {
      const focused = buttons.indexOf(document.activeElement);
      if (focused < 0) return;
      let target = focused;
      if (event.key === 'ArrowRight') target = (focused + 1) % buttons.length;
      else if (event.key === 'ArrowLeft') target = (focused + buttons.length - 1) % buttons.length;
      else if (event.key === 'Home') target = 0;
      else if (event.key === 'End') target = buttons.length - 1;
      else return;
      event.preventDefault();
      buttons[target].focus({ preventScroll: true });
      selectStep(target, true);
    });

    next.addEventListener('click', () => selectStep((step + 1) % buttons.length, true));

    play.addEventListener('click', () => {
      if (reduced.matches) return;
      paused = !paused;
      if (!paused) {
        // A manual choice gets a full reading interval when the tour resumes.
        if (root.dataset.manual === 'true') remaining = stepDuration;
        root.dataset.manual = 'false';
      }
      live.textContent = paused ? 'Guided tour paused.' : 'Guided tour resumed. Each step lasts nine seconds.';
      syncPlayback();
    });

    root.addEventListener('focusin', event => {
      if (play.contains(event.target) || paused) return;
      paused = true;
      syncPlayback();
    });

    function onReducedMotionChange() {
      stopClock();
      paused = true;
      remaining = stepDuration;
      root.dataset.manual = 'true';
      syncPlayback();
    }
    if (reduced.addEventListener) reduced.addEventListener('change', onReducedMotionChange);
    else reduced.addListener(onReducedMotionChange);

    document.addEventListener('visibilitychange', measureVisibility);
    window.addEventListener('pagehide', () => {
      pageActive = false;
      syncPlayback();
    });
    window.addEventListener('pageshow', () => {
      pageActive = true;
      measureVisibility();
    });

    root.style.setProperty('--os-step-duration', `${stepDuration}ms`);
    selectStep(0, false);
    nav.hidden = false;
    controls.hidden = false;
    root.dataset.ready = 'true';
    measureVisibility();

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        const entry = entries[0];
        inView = entry.isIntersecting && entry.intersectionRatio >= .2;
        syncPlayback();
      }, { threshold: [0, .2] });
      observer.observe(workspace);
    } else {
      window.addEventListener('scroll', measureVisibility, { passive: true });
      window.addEventListener('resize', measureVisibility, { passive: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOrderStory, { once: true });
  } else {
    initOrderStory();
  }
})();
